import * as React from "react";
import {memo, useState} from "react";
import {FeaturedModel, ProductModelNew, ProductQueryParams} from "../../../data/model/Product";
import {useNavigate} from "react-router-dom";
import {useDialogStore} from "../../../store/useAlertDialog";
import {useMutation} from "@tanstack/react-query";
import {addFeatured, deleteFeatured, deleteProduct, editFeatured, editProductNew} from "../../../data/api/Customer";
import {getFormattedData, IS_SMARTVENUES, QDRANT_BE_URL, SubmitHandler} from "../../../utils/Utils";
import {Button, HStack, Image, Input, Skeleton, Spinner, Stack, Text, Textarea, VStack} from "@chakra-ui/react";
import fallbackImage from "../../../assets/images/empty-state-wine.png";
import {FaArchive, FaClock, FaPen, FaStar, FaTrash} from "react-icons/all";
import {ColorsClass} from "../../../theme/colors";
import {FormProvider, useForm} from "react-hook-form";
import {FeaturedInputModel} from "../../../data/model/Producer";
import {yupResolver} from "@hookform/resolvers/yup";
import {featuredInputSchema} from "../../../data/schema/productSchema";
import {EventModelNew} from "../../../data/model/Event";


export const ProductCard = memo(({data, baseKey, doRefetch: refetch, isLoading}: {
    data?: ProductModelNew,
    baseKey?: string,
    doRefetch: () => void,
    isLoading?: boolean,

    isDraggable?: boolean;
    draggableId?: string;
    index?: number;
}) => {
    const navigate = useNavigate();

    const isAdmin = localStorage.getItem("userType") === 'admin';

    const {showDialog, closeDialog} = useDialogStore();

    const {mutateAsync} = useMutation({
        mutationKey: ['delete-product'],
        mutationFn: (id: string) => deleteProduct(id),
    });

    const {mutateAsync: archiveAsync, isPending: isPendingArchive} = useMutation({
        mutationKey: ['archive-product'],
        mutationFn: ({id, data}: { id: string, data: ProductQueryParams }) => editProductNew(id, data),
    });

    const {
        mediaList,
        id,
        name,
        categories,
        producer,
        createdAt,
        updatedAt,
        inEvidence,
        archived,
        mainImage,
    } = data || {};
    const {url} = mediaList?.filter((value) => value.isMain === true)?.[0] || {};
    const {name: producerName} = producer || {};
    const createDate = createdAt ? new Date(createdAt * 1000) : undefined;
    const updateDate = updatedAt ? new Date(updatedAt * 1000) : undefined;

    const formattedCreateDate = createDate && getFormattedData(createDate!);
    const formattedUpdateDate = updateDate && getFormattedData(updateDate!);

    return <HStack borderRadius={16} gap={0} w={'100%'} position={'relative'} backgroundColor={'surfaceContainer.light'}>
        <Image w={"20%"} m={'8px'} fallbackSrc={fallbackImage} alignSelf={'stretch'} aspectRatio={1}
               backgroundColor={'white'}
               objectFit={"contain"}
               borderRadius={"16px"}
               src={url || mainImage}/>

        <VStack flex={1} alignItems={'flex-start'} position={'relative'} p={'16px'} h={'100%'}>
            <Text fontSize={'8pt'}>{id}</Text>
            {producer &&
                <Text fontSize={'10pt'} mt={'-4px'} fontWeight={'semibold'}
                      color={'tertiary.700'}>{producerName}</Text>}
            <Text fontSize={'14pt'} mt={'-4px'} color={'primary.400'} fontWeight={'bold'}>{name}</Text>
            {createDate && <HStack alignItems={'center'}>
                <FaClock color={'#244c5a'} size={'14px'}/>
                <Text fontSize={'11pt'}>Aggiunto il: {formattedCreateDate}</Text>
            </HStack>}
            {updateDate && <HStack alignItems={'center'}>
                <FaPen color={'#244c5a'} size={'14px'}/>
                <Text fontSize={'11pt'}>Modificato il: {formattedUpdateDate}</Text>
            </HStack>}
            {categories && <HStack mt={'auto'}>
                {
                    categories.map((value, index) => <Stack key={`value_name_key_${index}`}
                                                            backgroundColor={'tertiary.200'} py={'4px'} px={'12px'}
                                                            borderRadius={'12px'}>
                        <Text fontSize={'10pt'}>{value.name}</Text>
                    </Stack>)
                }
            </HStack>}
        </VStack>
        <VStack alignSelf={'center'} alignItems={'center'} m={4}>
            {isAdmin && <Button w={"148px"} onClick={() => {
                showDialog({
                    title: "In evidenza",
                    description: "Inserisci le informazioni da mostrare nel prodotto in evidenza",
                    cancelButtonText: "Annulla",
                    onPressCancelButton: () => closeDialog(),
                    children: (
                        <VStack w={'100%'} h={'100%'} alignItems={'stretch'}>
                            <FeaturedDialogContent featuredItem={(data || {}).inEvidence} item={data || {}}
                                                   type={"product"}
                                                   close={async ({refetch: refetchBool}) => {
                                                       if (refetchBool) await refetch();
                                                       closeDialog();
                                                   }}/>
                        </VStack>
                    )
                });
            }} backgroundColor={inEvidence ? ColorsClass.orange : 'whitesmoke'} gap={2}>
                <FaStar color={inEvidence ? 'white' : 'black'} size={14}/>
                <Text fontSize={14} color={inEvidence ? 'white' : 'black'}>In evidenza</Text>
            </Button>}

            <Button w={'148px'} backgroundColor={'whitesmoke'} gap={2} onClick={() => navigate(`/products/edit/${id}`)}>
                <FaPen size={14}/>
                <Text fontSize={14}>Modifica</Text>
            </Button>
            <Button disabled={isPendingArchive} w={'148px'}
                    backgroundColor={archived ? ColorsClass.chatbotBlue : 'whitesmoke'} gap={2}
                    onClick={async () => {
                        await archiveAsync({
                            id: id || "", data: {
                                archived: !archived,
                            }
                        }).finally(() => {
                            refetch();
                        });

                    }}>
                {isPendingArchive ? <Spinner/> : <><FaArchive size={14} color={archived ? 'white' : 'black'}/>
                    <Text fontSize={14} color={archived ? 'white' : 'black'}>Archivia</Text></>}
            </Button>
            <Button w={'148px'} onClick={() => showDialog({
                title: "Elimina Prodotto",
                description: "Sei sicuro di voler eliminare questo prodotto?",
                cancelButtonText: "Annulla",
                confirmButtonText: "Elimina",
                onPressCancelButton: () => closeDialog(),
                onConfirmButtonClick: () => {
                    mutateAsync((id || '')).then(async () => {
                        await fetch(`${QDRANT_BE_URL}/delete/${id}?type=${IS_SMARTVENUES ? "smartvenues" : "enoteca"}`, {
                            method: "DELETE",
                            headers: { "Content-Type": "application/json" }
                        });

                        refetch();
                        closeDialog();
                    });
                }
            })} backgroundColor={'whitesmoke'} gap={2}>
                <FaTrash size={14} color={"#C60F0F"}/>
                <Text fontSize={14}>Elimina</Text>
            </Button>
        </VStack>
    </HStack>
});

export const ProductCardLoading = () => {
    return <HStack borderRadius={16} gap={0} w={'95%'} backgroundColor={'surfaceContainer.light'}>
        <Skeleton borderRadius={'16px'} w={'200px'} h={'200px'}/>

        <VStack flex={1} alignItems={'flex-start'} justifyContent={'center'} p={'16px'} h={'100%'}>
            <Skeleton height={'16px'} w={'60%'} borderRadius={'8px'}/>
            <Skeleton height={'24px'} mt={'8px'} w={'48%'} borderRadius={'8px'}/>
            <Skeleton height={'18px'} mt={''} w={'52%'} borderRadius={'8px'}/>
        </VStack>
    </HStack>
};

type FeaturedDialogContentProps =
    | {
    type: "product";
    item: ProductModelNew,
    featuredItem?: FeaturedModel,
    close: ({refetch}: { refetch: boolean }) => Promise<any>
}
    | {
    type: "event";
    item: EventModelNew,
    featuredItem?: FeaturedModel,
    close: ({refetch}: { refetch: boolean }) => Promise<any>
};

export const FeaturedDialogContent = ({type, item, featuredItem, close}: FeaturedDialogContentProps) => {
    // const {inEvidence} = item || {};
    const [buttonLoading, setButtonLoading] = useState<"add" | "edit" | "remove" | undefined>();

    const defaultValues: FeaturedInputModel = {
        productId: type === "product" ? item.id : undefined,
        eventId: type === "event" ? item.id : undefined,
        name: featuredItem?.name || (type === "product" ? item.name : item.title),
        chipText: featuredItem?.chipText || undefined,
        description: featuredItem?.description || (type === "product" ? item.shortDescription : item.description),
    };

    const {mutateAsync, isPending} = useMutation({
        mutationKey: ['featuredInput', item.id],
        mutationFn: (variables: FeaturedInputModel) => addFeatured(variables)
    });

    const {mutateAsync: editMutate, isPending: isPendingEdit} = useMutation({
        mutationKey: ['featuredInput:edit', item.id],
        mutationFn: ({id, variables}: { id: string, variables: FeaturedInputModel }) => editFeatured(id, variables),
    });

    const {mutateAsync: deleteMutate, isPending: isPendingDelete} = useMutation({
        mutationKey: ['featuredInput:delete', item.id],
        mutationFn: ({id}: { id: string }) => deleteFeatured(id),
    });

    const methods = useForm<FeaturedInputModel>({
        mode: "all",
        resolver: yupResolver(featuredInputSchema),
        defaultValues
    });

    const {handleSubmit, register} = methods || {};

    const onSubmit: SubmitHandler<FeaturedInputModel> = (data, event) => {
        const nativeEvent = event?.nativeEvent as SubmitEvent;
        const action = (nativeEvent.submitter as HTMLButtonElement)?.value;

        switch (action) {
            case "add":
                setButtonLoading('add');
                mutateAsync({
                    ...data,
                    startDate: data.startDate ? new Date(data.startDate).getTime() : undefined,
                    endDate: data.endDate ? new Date(data.endDate).getTime() : undefined,
                    productId: type === "product" ? item.id : undefined,
                    eventId: type === "event" ? item.id : undefined,
                }).finally(() => {
                    close?.({refetch: true}).finally(() => {
                        setButtonLoading(undefined);
                    });
                });
                break;
            case "edit":
                setButtonLoading('edit');
                editMutate({
                    id: featuredItem?.id || '', variables: {
                        ...data,
                        startDate: data.startDate ? new Date(data.startDate).getTime() : undefined,
                        endDate: data.endDate ? new Date(data.endDate).getTime() : undefined,
                        productId: type === "product" ? item.id : undefined,
                        eventId: type === "event" ? item.id : undefined,
                    }
                }).finally(() => {
                    close?.({refetch: true}).finally(() => {
                        setButtonLoading(undefined);
                    });
                });
                break;
            case "delete":
                setButtonLoading('remove');
                deleteMutate({id: featuredItem?.id || ''}).finally(() => {
                    close?.({refetch: true}).finally(() => {
                        setButtonLoading(undefined);
                    });
                });
                break;
        }
    };

    return (
        <FormProvider {...methods}>
            <form style={{width: '100%'}} onSubmit={handleSubmit(onSubmit)}>
                <VStack w={'100%'} gap={4}>
                    <Input
                        placeholder="Nome"
                        {...register("name")}
                    />
                    <Input
                        placeholder="Testo chip"
                        {...register("chipText")}
                    />
                    <Textarea
                        noOfLines={5}
                        placeholder="Descrizione"
                        {...register("description")}
                    />
                    <Input
                        placeholder="Data di inizio"
                        type={"date"}
                        defaultValue={featuredItem?.startDate ? new Date(featuredItem.startDate).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]}
                        {...register("startDate")}
                    />
                    <Input
                        placeholder="Data di fine"
                        type={"date"}
                        defaultValue={featuredItem?.endDate ? new Date(featuredItem.endDate).toISOString().split("T")[0] : undefined}
                        {...register("endDate")}
                    />
                    {!featuredItem &&
                        <Button disabled={(isPending || !!buttonLoading)} w={'100%'} name={"action"} value={"add"}
                                type="submit">{(isPending || buttonLoading === 'add') ? <Spinner/> : 'Salva'}</Button>}
                    {featuredItem && <>
                        <Button w={'100%'} disabled={(isPendingEdit || !!buttonLoading)} name={"action"} value={"edit"}
                                type="submit">{(isPendingEdit || buttonLoading === 'edit') ?
                            <Spinner/> : 'Modifica'}</Button>
                        <Button w={'100%'} disabled={(isPendingDelete || !!buttonLoading)} name={"action"}
                                value={"delete"} color={'red.400'}
                                type="submit">{(isPendingDelete || buttonLoading === 'remove') ?
                            <Spinner/> : 'Elimina'}</Button>
                    </>}
                </VStack>
            </form>
        </FormProvider>
    );
}
